import { useState, useRef, useCallback, useEffect } from 'react';
import { Loader2, Monitor, Tablet, Smartphone } from 'lucide-react';
import { apiClient } from '@/lib/api';
import type { LandingSection, LandingSettings } from '@easyflow/shared';

const DEVICE_WIDTHS: Record<string, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
};

interface PreviewIframeProps {
  sections: LandingSection[];
  settings: LandingSettings;
  title: string;
  selectedSectionId?: string | null;
  onSectionSelect?: (id: string | null) => void;
  previewDevice?: 'desktop' | 'tablet' | 'mobile';
  setPreviewDevice?: (d: 'desktop' | 'tablet' | 'mobile') => void;
}

export default function PreviewIframe({ sections, settings, title, selectedSectionId, onSectionSelect, previewDevice = 'desktop', setPreviewDevice }: PreviewIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState(false);
  const [iframeHeight, setIframeHeight] = useState('100%');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPayloadRef = useRef<string>('');

  const loadPreview = useCallback(async (secs: LandingSection[], setts: LandingSettings, t: string) => {
    const payload = JSON.stringify({ sections: secs, settings: setts, title: t });
    if (payload === lastPayloadRef.current) return;
    lastPayloadRef.current = payload;

    setLoading(true);
    try {
      const res = await apiClient.post<{ html: string }>('/landing-pages/preview-html', { sections: secs, settings: setts, title: t });
      const html = res.data?.html || '';

      if (iframeRef.current) {
        const doc = iframeRef.current.contentDocument;
        if (doc) {
          doc.open();
          doc.write(html);
          doc.close();
          setTimeout(() => {
            if (doc.body) {
              setIframeHeight(`${doc.body.scrollHeight}px`);
            }
          }, 500);
          setTimeout(() => {
            if (doc.body) {
              setIframeHeight(`${doc.body.scrollHeight}px`);
            }
          }, 2000);
        }
      }
    } catch (err) {
      console.error('Preview load failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sections.length === 0) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadPreview(sections, settings, title);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [sections, settings, title, loadPreview]);

  useEffect(() => {
    if (!iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage({ type: 'edg-section-highlight', sectionId: selectedSectionId || null }, '*');
  }, [selectedSectionId]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'edg-section-select' && onSectionSelect) {
        onSectionSelect(e.data.sectionId || null);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onSectionSelect]);

  if (sections.length === 0) return null;

  const isNotDesktop = previewDevice !== 'desktop';

  return (
    <div className="relative w-full" style={{ minHeight: '600px', isolation: 'isolate' }}>
      {setPreviewDevice && (
        <div className="flex items-center justify-center gap-1 mb-3 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg w-fit mx-auto">
          {([
            { key: 'desktop' as const, icon: Monitor, label: 'Desktop' },
            { key: 'tablet' as const, icon: Tablet, label: 'Tablet' },
            { key: 'mobile' as const, icon: Smartphone, label: 'Mobile' },
          ]).map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => setPreviewDevice(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                previewDevice === key
                  ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              title={label}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      )}

      <div
        className="transition-all duration-300 ease-out mx-auto"
        style={{
          maxWidth: DEVICE_WIDTHS[previewDevice],
          ...(isNotDesktop ? { border: '8px solid #1f2937', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' } : {}),
        }}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-950/80 z-10 rounded-lg">
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Caricamento preview premium...</span>
            </div>
          </div>
        )}
        <iframe
          ref={iframeRef}
          className="w-full border-0 rounded-lg relative z-0"
          style={{ height: iframeHeight, minHeight: '600px' }}
          sandbox="allow-scripts allow-same-origin"
          title="Landing Page Preview"
        />
      </div>
    </div>
  );
}
